/**
 * Small wording helpers shared by the attention copy (twin slice 1, W5).
 *
 * Times are written on the SITE's clock, because the reader is standing in
 * the building or talking to someone who is. When the site has not
 * declared a timezone the browser's zone is the only one there is, and the
 * time then says so -- "10:42 (device time)" -- rather than passing a
 * guess off as the hotel's clock.
 */
import { formatClock } from '../../screens/Devices/lift-reading';
import type { SiteTimeZone } from '../../screens/Tasks/site-timezone';
import { browserTimeZone, formatZonedClock, isKnownTimeZone } from '../../screens/Tasks/zoned-time';

export type { SiteTimeZone };

/** `HH:MM` on the site's clock, labelled when that clock is a guess. */
export function clockText(epochMs: number, clock: SiteTimeZone): string {
  // An unknown zone would make `Intl` throw inside a render; the resolver
  // never hands one over, but a hand-built clock in a caller might. It is
  // then the device's clock, and labelled as such.
  const known = isKnownTimeZone(clock.timeZone);
  const hhmm = formatZonedClock(epochMs, known ? clock.timeZone : browserTimeZone());
  return clock.source === 'site' && known ? hhmm : `${hhmm} (device time)`;
}

/**
 * `clockText` with the day named when it is not today: "23:58" today,
 * "yesterday 23:58", "27 Sep 23:58" -- the Overview heading's formatter
 * (`formatClock`, which `feedHeadingWords` uses), so the verdict and the
 * heading over it word one time alike. Without the day, a tab woken the
 * next morning read last night's "last update 23:58" as this morning's.
 *
 * `wallNowMs` is this device's wall clock, read only to tell which date
 * it is on the site's clock; it is never subtracted from the stamp. The
 * labelling is `clockText`'s: "(device time)" whenever the clock is not
 * the site's own.
 */
export function dayClockText(epochMs: number, clock: SiteTimeZone, wallNowMs: number): string {
  const known = isKnownTimeZone(clock.timeZone);
  // `formatClock` labels an unknown zone itself; a known zone that is the
  // browser's guess is labelled here.
  const when = formatClock(epochMs, known ? clock.timeZone : null, wallNowMs);
  return clock.source === 'site' || !known ? when : `${when} (device time)`;
}

/**
 * "Level 9" for the map name `L9`, "Level 4M" for `L4M`; any other name as
 * it is. Null in, null out: a robot whose floor is not known is never
 * given one.
 */
export function levelWords(map: string | null | undefined): string | null {
  if (!map) return null;
  const m = /^L0*(\d+M?)$/i.exec(map.trim());
  return m ? `Level ${m[1]!.toUpperCase()}` : map;
}

/** "phantas_01", "phantas_01 and phantas_02", "a, b and c". */
export function joinNames(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}
